import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { tokens } from "../../../theme";
import Button from "../../../components/common/Button";

type MemoryBookStatus = "overdue" | "in_danger" | "safe";

interface MemoryBookEntry {
  id: number;
  deceasedName: string;
  deceasedAvatarUrl?: string;
  timeInfo: string;
  commentCount: number;
  progressPercent: number;
  status: MemoryBookStatus;
}

interface StaffAssignment {
  id: number;
  avatarUrl?: string;
  name: string;
  assignedCount: number;
}

interface MemoryBookReassignModalProps {
  open: boolean;
  onClose: () => void;
  entry: MemoryBookEntry | null;
  staffOptions: StaffAssignment[];
  currentAssigneeId: number;
  onReassign: (entryId: number, staffId: number) => void;
  onViewBook: () => void;
}

const STATUS_META: Record<
  MemoryBookStatus,
  { label: string; color: string; bg: string }
> = {
  overdue: {
    label: "This book is overdue",
    color: "#E11D48",
    bg: "#FAD7DF",
  },
  in_danger: {
    label: "This book is in danger",
    color: "#f59e0b",
    bg: "#FEF0C7",
  },
  safe: {
    label: "This book is safe",
    color: "#10b981",
    bg: "#D1FAE5",
  },
};

const MemoryBookReassignModal = ({
  open,
  onClose,
  entry,
  staffOptions,
  currentAssigneeId,
  onReassign,
  onViewBook,
}: MemoryBookReassignModalProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!entry) return null;

  const meta = STATUS_META[entry.status];
  const currentAssignee = staffOptions.find((s) => s.id === currentAssigneeId);

  // Parse timeInfo to derive a due date label and days passed (best-effort)
  const isDaysAgo = entry.timeInfo.toLowerCase().includes("days ago");
  const daysMatch = entry.timeInfo.match(/(\d+)/);
  const daysPassed = daysMatch ? parseInt(daysMatch[1]) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "30px",
          backgroundColor: colors.primary[800],
          backgroundImage: "none",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
        },
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          pt: 2,
          pb: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: colors.primary[700],
            textTransform: "uppercase",
          }}
        >
          Reassign
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: colors.primary[700] }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
        <Box
          sx={{
            position: "relative",
            mb: 2.5,
            pt: 1.8,
          }}
        >
          {/* Floating status badge */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 28,
              zIndex: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              px: 1.8,
              py: 0.7,
              borderRadius: "50px",
              backgroundColor: meta.bg,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: meta.color,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 600,
                color: meta.color,
              }}
            >
              {meta.label}
            </Typography>
          </Box>

          {/* Deceased info card */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              pt: 3,
              borderRadius: "20px",
              border: `1px solid ${meta.color}40`,
              backgroundColor: colors.primary[900],
            }}
          >
            <Avatar
              src={entry.deceasedAvatarUrl}
              sx={{
                width: 72,
                height: 72,
                flexShrink: 0,
                borderRadius: "50px",
              }}
            >
              {entry.deceasedName[0]}
            </Avatar>

            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  color: colors.primary[700],
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {entry.deceasedName}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: colors.primary[100],
                  mb: 0.2,
                }}
              >
                Due Date:{" "}
                <Box component="span" sx={{ color: colors.primary[700] }}>
                  {entry.timeInfo}
                </Box>
              </Typography>

              {isDaysAgo && daysPassed !== null && (
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: colors.primary[100],
                  }}
                >
                  Days passed:{" "}
                  <Box component="span" sx={{ color: colors.primary[700] }}>
                    {daysPassed} {daysPassed === 1 ? "day" : "days"}
                  </Box>
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Current Assignee */}
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: colors.primary[700],
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Current Assignee
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            borderRadius: "16px",
            backgroundColor: colors.primary[900],
            mb: 2.5,
          }}
        >
          <Avatar
            src={currentAssignee?.avatarUrl}
            sx={{ width: 44, height: 44 }}
          >
            {currentAssignee?.name[0] ?? "?"}
          </Avatar>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: colors.primary[100],
            }}
          >
            {currentAssignee?.name ?? "Unassigned"}
          </Typography>
        </Box>

        {/* Available Options */}
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: colors.primary[700],
            textTransform: "uppercase",
            mb: 1.2,
          }}
        >
          Available Options
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
          {staffOptions.map((staff) => {
            // const isSelected = staff.id === currentAssigneeId;
            return (
              <Box
                key={staff.id}
                onClick={() => onReassign(entry.id, staff.id)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "50%",
                  border: `3px solid ${colors.primary[500] + "60"}`,
                  transition: "border-color 0.2s ease, transform 0.15s ease",
                  "&:hover": {
                    borderColor: meta.color,
                    transform: "scale(1.08)",
                  },
                }}
              >
                <Avatar
                  src={staff.avatarUrl}
                  sx={{
                    width: 60,
                    height: 60,
                    border: `2px solid ${colors.primary[800]}`,
                  }}
                >
                  {staff.name[0]}
                </Avatar>
              </Box>
            );
          })}
        </Box>

        {/* View Book button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={onViewBook}
            sx={{
              px: 3.5,
              py: 1.2,
              fontWeight: 700,
              fontSize: "0.85rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            View Book
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MemoryBookReassignModal;
